import { useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Table,
  type Column,
  type ContextMenuItem,
  Button,
  SearchInput,
  ConfirmDialog,
  Modal,
  ContactOverlay,
  Badge,
  SendBatchModal,
  MailingListFormModal,
  MailingListCampaigns,
  MemberAnswerStrip,
  ANSWER_STRIP_HINT,
  Tooltip,
} from "../components/Common";
import { mailingListsAPI, contactsAPI, venuesAPI, bandsAPI } from "../api/endpoints";
import { useOpenRequest } from "../hooks/useOpenRequest";
import type { MailingList, Contact, Venue, MailingListContact } from "../types";
import {
  Send,
  Plus,
  Trash2,
  Users,
  ArrowLeft,
  UserMinus,
  ExternalLink,
  MapPin,
  Pencil,
  AlertTriangle,
  Music,
  Copy,
  Check,
  MailCheck,
} from "lucide-react";
import { format } from "date-fns";

export function MailingListsPage() {
  const queryClient = useQueryClient();

  // --- List view state ---
  const [searchText, setSearchText] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingList, setEditingList] = useState<MailingList | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MailingList | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- Detail view state ---
  // In the URL, not in component state: a campaign's follow-up todo links straight to its list
  // (`?list=<id>&campaign=<id>`), and a detail view is then linkable and survives a reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedListId = searchParams.get("list");
  const openCampaignId = searchParams.get("campaign");

  const setSelectedListId = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set("list", id);
      else next.delete("list");
      // Another list's campaign is not this one's: the open campaign never outlives the list
      if (id !== selectedListId) next.delete("campaign");
      setSearchParams(next);
    },
    [searchParams, setSearchParams, selectedListId]
  );

  const setOpenCampaignId = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set("campaign", id);
      else next.delete("campaign");
      // Opening and closing a campaign is not navigation: it must not fill the back button
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // --- Contact overlay state ---
  const [isContactOverlayOpen, setIsContactOverlayOpen] = useState(false);
  const [overlayVenue, setOverlayVenue] = useState<Venue | null>(null);
  const [overlayContacts, setOverlayContacts] = useState<Contact[]>([]);
  const [overlayContactId, setOverlayContactId] = useState<string | undefined>(undefined);
  // Index into `members` — the three tables read top to bottom — so the overlay's arrows walk it
  const [overlayIndex, setOverlayIndex] = useState<number | null>(null);
  const [isOverlayNavigating, setIsOverlayNavigating] = useState(false);
  // The overlay opens on a venue + contacts fetch; this retires one the user closed meanwhile
  const openRequest = useOpenRequest();
  const [venuePickerContact, setVenuePickerContact] = useState<Contact | null>(null);
  const [venuePickerVenues, setVenuePickerVenues] = useState<Venue[]>([]);

  // --- Detail contact search ---
  const [detailContactSearch, setDetailContactSearch] = useState("");

  // The member tables sort here rather than inside `Table` (`serverSort`): the overlay's arrows
  // walk `members`, so a header click has to move the rows there — and the three tables are one
  // list, which is also why they now sort together.
  const [memberSort, setMemberSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

  // --- Copy emails state ---
  const [copied, setCopied] = useState(false);

  // --- Batch send state ---
  const [isBatchSendOpen, setIsBatchSendOpen] = useState(false);

  // --- Remove contact state ---
  const [removeContactTarget, setRemoveContactTarget] = useState<MailingListContact | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // --- Queries ---
  const PAGE_SIZE = 3000;
  const { data: mailingListsData, isLoading } = useQuery({
    queryKey: ["mailing-lists", searchText],
    queryFn: async ({ signal }) => {
      if (searchText) {
        const res = await mailingListsAPI.search(searchText, signal);
        return res.data;
      }
      const res = await mailingListsAPI.list({ page_size: PAGE_SIZE }, signal);
      return res.data;
    },
  });
  const mailingLists = useMemo(() => mailingListsData?.items ?? [], [mailingListsData]);
  // The table sorts client-side on row[key]: a list without campaigns sorts as "" rather than null
  const listRows = useMemo(
    () => mailingLists.map((l) => ({ ...l, last_campaign_at: l.last_campaign_at ?? "" })),
    [mailingLists]
  );
  const totalMailingLists = mailingListsData?.total ?? 0;

  const loadedList = useMemo(
    () => mailingLists.find((l) => l._id === selectedListId) ?? null,
    [mailingLists, selectedListId]
  );
  // A link from a todo can name a list this page has not loaded — a search term narrows the
  // listing, and it is capped — so fall back to fetching that one list by id. Keyed under
  // ["mailing-lists", ...] so every invalidation of the listing refreshes it too.
  const { data: linkedList } = useQuery({
    queryKey: ["mailing-lists", "by-id", selectedListId],
    queryFn: async () => (await mailingListsAPI.getById(selectedListId as string)).data,
    enabled: !!selectedListId && !loadedList && !isLoading,
  });
  const selectedList = loadedList ?? (linkedList?._id === selectedListId ? linkedList : null);

  const { data: bands = [] } = useQuery({
    queryKey: ["bands"],
    queryFn: async () => (await bandsAPI.list()).data.items,
  });

  const bandMap = useMemo(() => {
    const map: Record<string, string> = {};
    bands.forEach((b) => { map[b._id] = b.name; });
    return map;
  }, [bands]);

  // Fetch contact objects for the selected list's contacts (batch by IDs). Summary only: full
  // contacts embed their mail and a whole list of them times out; the overlay fetches its own.
  const selectedListContactIds = useMemo(
    () => selectedList?.contacts.map((c) => c.contact_id) ?? [],
    [selectedList]
  );

  const { data: listContacts = [] } = useQuery({
    queryKey: ["contacts-batch", selectedListContactIds],
    queryFn: async () => {
      if (selectedListContactIds.length === 0) return [];
      return (await contactsAPI.batchGet(selectedListContactIds, { summary: true })).data;
    },
    enabled: selectedListContactIds.length > 0,
  });

  // Did the venue answer each campaign, for each member (docs/CAMPAIGNS.md)? The verdict is the
  // venue's, so it is folded server-side: the stored flag sits on one mail per venue per campaign.
  const { data: memberAnswers } = useQuery({
    queryKey: ["mailing-list-member-answers", selectedListId],
    queryFn: async ({ signal }) =>
      (await mailingListsAPI.memberAnswers(selectedListId as string, signal)).data,
    enabled: !!selectedListId,
  });
  const answerAxis = useMemo(() => memberAnswers?.campaigns ?? [], [memberAnswers]);

  // Map contact IDs to full Contact objects for the selected list. The answer strip and its two
  // counts ride on the row because Table sorts client-side on row[key].
  type ListContact = Contact & {
    _listEmail: string;
    _answerStrip: string;
    _campaignsMailed: number;
    _campaignsAnswered: number;
  };

  const sortMembers = useCallback(
    (rows: ListContact[]): ListContact[] => {
      if (!memberSort) return rows;
      const { key, direction } = memberSort;
      const value = (row: ListContact): string | number | null => {
        const raw = (row as unknown as Record<string, unknown>)[key];
        if (raw == null || raw === "") return null;
        return typeof raw === "number" ? raw : String(raw).toLowerCase();
      };
      return [...rows].sort((a, b) => {
        const av = value(a);
        const bv = value(b);
        if (av === bv) return 0;
        if (av === null) return 1; // blanks last, whichever way the column points
        if (bv === null) return -1;
        return (av < bv ? -1 : 1) * (direction === "asc" ? 1 : -1);
      });
    },
    [memberSort]
  );

  const { activeContacts, doNotContactList, expiredList } = useMemo(() => {
    if (!selectedList) return { activeContacts: [] as ListContact[], doNotContactList: [] as ListContact[], expiredList: [] as ListContact[] };
    const contactMap = new Map(listContacts.map((c) => [c._id, c]));
    const strips = memberAnswers?.members ?? {};
    let contacts = selectedList.contacts
      .map((mc) => {
        const full = contactMap.get(mc.contact_id);
        if (!full) return null;
        const strip = strips[mc.contact_id] ?? "";
        return {
          ...full,
          _listEmail: mc.email,
          _answerStrip: strip,
          _campaignsMailed: [...strip].filter((c) => c !== "-").length,
          _campaignsAnswered: [...strip].filter((c) => c === "a").length,
        };
      })
      .filter((c): c is ListContact => c !== null);
    if (detailContactSearch) {
      const s = detailContactSearch.toLowerCase();
      contacts = contacts.filter(
        (c) =>
          c.name?.toLowerCase().includes(s) ||
          c._listEmail?.toLowerCase().includes(s) ||
          c.email?.toLowerCase().includes(s)
      );
    }
    const active: ListContact[] = [];
    const dnc: ListContact[] = [];
    const expired: ListContact[] = [];
    for (const c of contacts) {
      if (c.flags === "do_not_contact") {
        dnc.push(c);
      } else if (c.flags === "expired") {
        expired.push(c);
      } else {
        active.push(c);
      }
    }
    return {
      activeContacts: sortMembers(active),
      doNotContactList: sortMembers(dnc),
      expiredList: sortMembers(expired),
    };
  }, [selectedList, listContacts, detailContactSearch, memberAnswers, sortMembers]);

  // The rows in the order the three tables show them, so the overlay's prev / next walks the whole
  // list — search included — without re-reading the tables.
  const members = useMemo(
    () => [...activeContacts, ...doNotContactList, ...expiredList],
    [activeContacts, doNotContactList, expiredList]
  );

  // Members a campaign will reach, whatever the search box shows. Only a preview: the server
  // resolves the recipients itself when the campaign is sent.
  const sendableCount = useMemo(() => {
    if (!selectedList) return 0;
    const contactMap = new Map(listContacts.map((c) => [c._id, c]));
    const addresses = new Set<string>();
    for (const mc of selectedList.contacts) {
      const c = contactMap.get(mc.contact_id);
      if (!c?.email || c.flags === "expired" || c.flags === "do_not_contact") continue;
      addresses.add(c.email.trim().toLowerCase());
    }
    return addresses.size;
  }, [selectedList, listContacts]);

  // --- Handlers ---
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await mailingListsAPI.delete(deleteTarget._id);
      queryClient.invalidateQueries({ queryKey: ["mailing-lists"] });
      if (selectedListId === deleteTarget._id) {
        setSelectedListId(null);
      }
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, queryClient, selectedListId, setSelectedListId]);

  const handleRemoveContact = useCallback(async () => {
    if (!selectedList || !removeContactTarget) return;
    setIsRemoving(true);
    try {
      const updatedContacts = selectedList.contacts.filter(
        (c) => c.contact_id !== removeContactTarget.contact_id
      );
      await mailingListsAPI.update(selectedList._id, { contacts: updatedContacts });
      queryClient.invalidateQueries({ queryKey: ["mailing-lists"] });
    } finally {
      setIsRemoving(false);
      setRemoveContactTarget(null);
    }
  }, [selectedList, removeContactTarget, queryClient]);

  // By id, one venue at a time: there is no page of /venues to look a member's venue up in. The
  // account holds tens of thousands and a list's members are spread across all of them, so a
  // `list({ page_size: 1000 })` missed most of them and opened a venue-less overlay instead.
  const resolveVenue = useCallback(
    async (venueId: string): Promise<Venue | null> => {
      try {
        return (await venuesAPI.getById(venueId)).data;
      } catch (err) {
        console.error("Failed to fetch venue for overlay:", err);
        return null;
      }
    },
    []
  );

  // A row opens its *venue*: the venue record plus every contact attached to it, like the Venues
  // and Todo pages. The member's own record from the list is a summary (no communications), and
  // its colleagues are what makes the thread readable, so both are refetched here.
  const openVenueOverlay = useCallback(async (contact: Contact, venue: Venue | null, token?: number) => {
    const openToken = token ?? openRequest.begin();
    try {
      const venueContactIds = venue?.contact_ids ?? [];
      let contacts = venueContactIds.length > 0 ? (await contactsAPI.batchGet(venueContactIds)).data : [];
      if (!contacts.some((c) => c._id === contact._id)) {
        // No venue, or a link the venue does not mirror: the member must still be in the overlay
        contacts = [...contacts, (await contactsAPI.getById(contact._id)).data];
      }
      // Contacts carry their mail bodies: this runs for seconds, and the user may have closed
      // the overlay or moved to another member since. Opening it here is what brought a
      // dismissed overlay back on screen.
      if (!openRequest.isCurrent(openToken)) return;
      setOverlayVenue(venue);
      setOverlayContacts(contacts);
      setOverlayContactId(contact._id);
      setIsContactOverlayOpen(true);
    } catch (err) {
      console.error("Failed to open venue overlay:", err);
      toast.error("Failed to open this venue");
    }
  }, [openRequest]);

  // `autoPickVenue` skips the multi-venue picker: a modal must not interrupt arrow navigation.
  const openMember = useCallback(
    async (contact: Contact, index: number | null, autoPickVenue = false) => {
      // One token for the whole open, venue lookup included, so a close during either fetch
      // retires it.
      const token = openRequest.begin();
      setOverlayIndex(index);
      const venueIds = contact.venue_ids ?? [];
      if (venueIds.length > 1 && !autoPickVenue) {
        setVenuePickerVenues([]);
        setVenuePickerContact(contact);
        const resolved = await Promise.all(venueIds.map(resolveVenue));
        if (!openRequest.isCurrent(token)) return;
        setVenuePickerVenues(resolved.filter((v): v is Venue => v !== null));
        return;
      }
      setIsOverlayNavigating(true);
      try {
        const venue = venueIds.length > 0 ? await resolveVenue(venueIds[0]) : null;
        if (!openRequest.isCurrent(token)) return;
        // Said out loud rather than shrugged off: a silently venue-less overlay is exactly what
        // the old lookup produced, and it reads as the row simply not working.
        if (venueIds.length > 0 && !venue) toast.error("Could not load this member's venue");
        await openVenueOverlay(contact, venue, token);
      } finally {
        setIsOverlayNavigating(false);
      }
    },
    [resolveVenue, openVenueOverlay, openRequest]
  );

  const handleMemberSort = useCallback((key: string, direction: "asc" | "desc" | null) => {
    setMemberSort(direction ? { key, direction } : null);
  }, []);

  const openMemberRow = useCallback(
    (row: Contact) => {
      const index = members.findIndex((m) => m._id === row._id);
      openMember(row, index === -1 ? null : index);
    },
    [members, openMember]
  );

  const handleOverlayNavigate = useCallback(
    (direction: "prev" | "next") => {
      if (overlayIndex === null || isOverlayNavigating) return;
      const target = overlayIndex + (direction === "next" ? 1 : -1);
      const member = members[target];
      if (!member) return;
      openMember(member, target, true);
    },
    [overlayIndex, isOverlayNavigating, members, openMember]
  );

  const overlayHasPrev = overlayIndex !== null && overlayIndex > 0;
  const overlayHasNext = overlayIndex !== null && overlayIndex < members.length - 1;

  // The row the open overlay is on, so arrow navigation is visible in the table behind it
  const memberRowClassName = useCallback(
    (row: ListContact): string | undefined =>
      isContactOverlayOpen && row._id === overlayContactId
        ? "ring-1 ring-inset ring-primary-500 bg-primary-900/10"
        : undefined,
    [isContactOverlayOpen, overlayContactId]
  );

  // --- Table columns for list overview ---
  const listColumns: Column<MailingList>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        sortable: true,
        render: (row) => (
          <div className="font-medium text-dark-text-primary">{row.name}</div>
        ),
      },
      {
        key: "description",
        label: "Description",
        render: (row) => (
          <span className="text-dark-text-secondary text-sm">
            {row.description || "—"}
          </span>
        ),
      },
      {
        key: "band_ids",
        label: "Bands",
        render: (row) => (
          <div className="flex flex-wrap gap-1">
            {row.band_ids && row.band_ids.length > 0
              ? row.band_ids.map((id) => (
                  <Badge key={id} variant="info" className="text-xs py-0">
                    {bandMap[id] || id}
                  </Badge>
                ))
              : <span className="text-dark-text-muted">—</span>}
          </div>
        ),
      },
      {
        key: "contacts",
        label: "Contacts",
        sortable: true,
        width: "100px",
        render: (row) => (
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-dark-text-muted" />
            <span>{row.contacts.length}</span>
          </div>
        ),
      },
      {
        key: "campaign_count",
        label: "Campaigns",
        sortable: true,
        width: "110px",
        render: (row) => (
          <span className={row.campaign_count ? "" : "text-dark-text-muted"}>
            {row.campaign_count || "—"}
          </span>
        ),
      },
      {
        key: "last_campaign_at",
        label: "Last campaign",
        sortable: true,
        render: (row) =>
          row.last_campaign_at ? (
            <span className="text-sm" title={row.last_campaign_name ?? undefined}>
              <span className="text-dark-text-primary">{row.last_campaign_name}</span>
              <span className="text-dark-text-secondary">
                {" · "}
                {format(new Date(row.last_campaign_at), "dd MMM yyyy")}
              </span>
            </span>
          ) : (
            <span className="text-dark-text-muted">—</span>
          ),
      },
      {
        key: "updated_at",
        label: "Updated",
        sortable: true,
        width: "140px",
        render: (row) => (
          <span className="text-dark-text-secondary text-sm">
            {format(new Date(row.updated_at), "dd MMM yyyy")}
          </span>
        ),
      },
    ],
    [bandMap]
  );

  const rowContextMenu = useCallback(
    (row: MailingList): ContextMenuItem[] => [
      {
        label: "Edit",
        icon: <Pencil className="w-4 h-4" />,
        onClick: () => setEditingList(row),
      },
      {
        label: "Delete",
        icon: <Trash2 className="w-4 h-4" />,
        onClick: () => setDeleteTarget(row),
        danger: true,
      },
    ],
    []
  );

  // --- Contact table columns for detail view ---
  const contactColumns: Column<ListContact>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        sortable: true,
        render: (row) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-dark-text-primary">
              {row.name || "—"}
            </span>
            {row.flags === "do_not_contact" && (
              <Badge variant="error" className="text-xs">do not contact</Badge>
            )}
            {row.flags === "expired" && (
              <Badge variant="warning" className="text-xs">expired</Badge>
            )}
          </div>
        ),
      },
      {
        key: "email",
        label: "Email",
        sortable: true,
        render: (row) => (
          <span className="text-dark-text-secondary text-sm">
            {row._listEmail || row.email || "—"}
          </span>
        ),
      },
      {
        key: "_campaignsMailed",
        label: "Campaigns",
        sortable: true,
        width: "110px",
        render: (row) => (
          <span className={row._campaignsMailed ? "" : "text-dark-text-muted"}>
            {row._campaignsMailed || "—"}
          </span>
        ),
      },
      {
        key: "_campaignsAnswered",
        label: "Answers",
        sortable: true,
        labelRender: () => (
          <Tooltip content={ANSWER_STRIP_HINT}>
            <span className="underline decoration-dotted">Answers</span>
          </Tooltip>
        ),
        render: (row) => <MemberAnswerStrip strip={row._answerStrip} campaigns={answerAxis} />,
      },
    ],
    [answerAxis]
  );

  const contactContextMenu = useCallback(
    (row: ListContact): ContextMenuItem[] => [
      {
        label: "Open Venue",
        icon: <ExternalLink className="w-4 h-4" />,
        onClick: () => openMemberRow(row),
      },
      {
        label: "Remove from List",
        icon: <UserMinus className="w-4 h-4" />,
        onClick: () =>
          setRemoveContactTarget({ contact_id: row._id, email: row._listEmail }),
        danger: true,
      },
    ],
    [openMemberRow]
  );

  // --- Render ---
  if (selectedList) {
    return (
      <div className="h-full flex flex-col animate-fadeIn">
        <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-6 flex-1">
          {/* Header */}
          <div className="mb-6 animate-slideDown">
            <button
              onClick={() => setSelectedListId(null)}
              className="flex items-center gap-1.5 text-sm text-dark-text-secondary hover:text-dark-text-primary transition-colors mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Lists
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Send className="w-8 h-8 text-dark-accent" />
                <div>
                  <h1 className="text-2xl font-bold text-dark-text-primary">
                    {selectedList.name}
                  </h1>
                  {selectedList.description && (
                    <p className="text-sm text-dark-text-secondary mt-0.5">
                      {selectedList.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const contactMap = new Map(listContacts.map((c) => [c._id, c]));
                    const rows = selectedList.contacts.map((mc) => {
                      const full = contactMap.get(mc.contact_id);
                      return `${full?.name || ""}\t${mc.email}`;
                    });
                    navigator.clipboard.writeText(rows.join("\n"));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied!" : "Copy Emails"}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsBatchSendOpen(true)}
                  disabled={sendableCount === 0}
                >
                  <MailCheck className="w-4 h-4" />
                  Send Emails
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditingList(selectedList)}>
                  <Pencil className="w-4 h-4" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleteTarget(selectedList)}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3">
              <Badge variant="info">
                <Users className="w-3 h-3 mr-1 inline" />
                {selectedList.contacts.length} contact{selectedList.contacts.length !== 1 ? "s" : ""}
              </Badge>
              <Badge variant="default">
                {selectedList.campaign_count} campaign{selectedList.campaign_count !== 1 ? "s" : ""}
              </Badge>
              {selectedList.band_ids?.map((id) => (
                <Badge key={id} variant="info" className="text-xs">
                  <Music className="w-3 h-3 mr-1 inline" />
                  {bandMap[id] || id}
                </Badge>
              ))}
            </div>
          </div>

          <MailingListCampaigns
            mailingListId={selectedList._id}
            openCampaignId={openCampaignId}
            onOpenCampaignChange={setOpenCampaignId}
          />

          {/* Search + Add */}
          <div className="card animate-scaleIn mb-3">
            <div className="p-4 flex items-center gap-3">
              <SearchInput
                value={detailContactSearch}
                onChange={setDetailContactSearch}
                placeholder="Search contacts in mailing list..."
                className="flex-1"
              />
              <AddContactToList
                mailingList={selectedList}
                onAdded={() => queryClient.invalidateQueries({ queryKey: ["mailing-lists"] })}
              />
            </div>
          </div>

          {/* Contacts grouped by status */}
          {activeContacts.length === 0 && doNotContactList.length === 0 && expiredList.length === 0 ? (
            <div className="card animate-scaleIn p-8 text-center text-dark-text-secondary">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No contacts in this list yet.</p>
              <p className="text-sm mt-1">Add contacts to start building your mailing list.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {activeContacts.length > 0 && (
                <div className="animate-scaleIn">
                  <div className="card">
                    <Table
                      data={activeContacts}
                      columns={contactColumns}
                      searchable={false}
                      emptyMessage="No active contacts"
                      serverSort
                      sortConfig={memberSort}
                      onSort={handleMemberSort}
                      onRowModClick={openMemberRow}
                      rowClassName={memberRowClassName}
                      rowContextMenu={contactContextMenu}
                    />
                  </div>
                </div>
              )}
              {doNotContactList.length > 0 && (
                <div className="animate-scaleIn">
                  <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-semibold text-red-400">Do Not Contact</span>
                    <span className="text-xs text-dark-text-muted">({doNotContactList.length})</span>
                  </div>
                  <div className="card border border-red-500/20">
                    <Table
                      data={doNotContactList}
                      columns={contactColumns}
                      searchable={false}
                      emptyMessage=""
                      serverSort
                      sortConfig={memberSort}
                      onSort={handleMemberSort}
                      onRowModClick={openMemberRow}
                      rowClassName={memberRowClassName}
                      rowContextMenu={contactContextMenu}
                    />
                  </div>
                </div>
              )}
              {expiredList.length > 0 && (
                <div className="animate-scaleIn">
                  <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-semibold text-amber-400">Expired</span>
                    <span className="text-xs text-dark-text-muted">({expiredList.length})</span>
                  </div>
                  <div className="card border border-amber-500/20">
                    <Table
                      data={expiredList}
                      columns={contactColumns}
                      searchable={false}
                      emptyMessage=""
                      serverSort
                      sortConfig={memberSort}
                      onSort={handleMemberSort}
                      onRowModClick={openMemberRow}
                      rowClassName={memberRowClassName}
                      rowContextMenu={contactContextMenu}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Venue picker modal */}
        <Modal
          isOpen={venuePickerContact !== null}
          onClose={() => {
            setVenuePickerContact(null);
            setVenuePickerVenues([]);
          }}
          title="Select Venue"
          size="sm"
        >
          <div className="space-y-2 p-2">
            <p className="text-sm text-dark-text-secondary mb-3">
              This contact is linked to multiple venues. Select one to continue:
            </p>
            {venuePickerVenues.length === 0 && (
              <p className="text-sm text-dark-text-muted text-center py-3">Loading venues...</p>
            )}
            {venuePickerVenues.map((venue) => (
              <button
                key={venue._id}
                className="w-full text-left px-4 py-3 rounded-lg bg-dark-bg hover:bg-dark-border transition-colors flex items-center gap-3"
                onClick={() => {
                  const contact = venuePickerContact;
                  setVenuePickerContact(null);
                  setVenuePickerVenues([]);
                  if (contact) {
                    openVenueOverlay(contact, venue);
                  }
                }}
              >
                <MapPin className="w-4 h-4 text-primary-400 shrink-0" />
                <div>
                  <div className="font-medium text-dark-text-primary">{venue.name}</div>
                  {venue.address?.city && (
                    <div className="text-xs text-dark-text-muted">{venue.address.city}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Modal>

        {/* Contact overlay */}
        <ContactOverlay
          isOpen={isContactOverlayOpen}
          onClose={() => {
            openRequest.cancel();
            setIsContactOverlayOpen(false);
            setOverlayVenue(null);
            setOverlayContacts([]);
            setOverlayContactId(undefined);
            setOverlayIndex(null);
            setIsOverlayNavigating(false);
          }}
          venue={overlayVenue}
          contacts={overlayContacts}
          initialContactId={overlayContactId}
          bands={bands}
          hasPrev={overlayHasPrev}
          hasNext={overlayHasNext}
          isNavigating={isOverlayNavigating}
          onNavigate={handleOverlayNavigate}
        />

        {/* Remove contact confirm */}
        <ConfirmDialog
          isOpen={removeContactTarget !== null}
          onClose={() => setRemoveContactTarget(null)}
          onConfirm={handleRemoveContact}
          title="Remove Contact"
          message={`Remove ${removeContactTarget?.email ?? "this contact"} from "${selectedList.name}"?`}
          confirmLabel="Remove"
          danger
          isLoading={isRemoving}
        />

        {/* Delete list confirm */}
        <ConfirmDialog
          isOpen={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          title="Delete Mailing List"
          message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          danger
          isLoading={isDeleting}
        />

        {/* Edit modal */}
        {editingList && (
          <MailingListFormModal
            mailingList={editingList}
            bands={bands}
            onClose={() => setEditingList(null)}
            onSaved={() => {
              setEditingList(null);
              queryClient.invalidateQueries({ queryKey: ["mailing-lists"] });
            }}
          />
        )}

        {/* Batch send modal */}
        <SendBatchModal
          isOpen={isBatchSendOpen}
          onClose={() => setIsBatchSendOpen(false)}
          mailingListId={selectedList._id}
          recipientCount={sendableCount}
          listName={selectedList.name}
          lastCampaignName={selectedList.last_campaign_name}
          lastCampaignAt={selectedList.last_campaign_at}
        />
      </div>
    );
  }

  // --- List overview ---
  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-6 flex-1">
        {/* Header */}
        <div className="mb-6 animate-slideDown">
          <div className="flex items-center gap-3 mb-2">
            <Send className="w-8 h-8 text-dark-accent" />
            <h1 className="text-2xl font-bold text-dark-text-primary">Mailing Lists</h1>
          </div>
          <p className="text-sm text-dark-text-secondary">
            Manage your email distribution lists
          </p>
        </div>

        {/* Content card */}
        <div className="card animate-scaleIn">
          <div className="p-4 border-b border-dark-border flex items-center gap-3">
            <SearchInput
              value={searchText}
              onChange={setSearchText}
              placeholder="Search lists..."
              className="flex-1"
            />
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4" />
              New List
            </Button>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-dark-text-secondary">Loading...</div>
          ) : mailingLists.length === 0 ? (
            <div className="p-8 text-center text-dark-text-secondary">
              <Send className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{searchText ? "No lists found matching your search." : "No mailing lists yet."}</p>
              {!searchText && (
                <p className="text-sm mt-1">Create your first list to start organizing contacts.</p>
              )}
            </div>
          ) : (
            <>
              {totalMailingLists > mailingLists.length && (
                <div className="px-4 py-2 text-sm text-amber-400 bg-amber-500/10 border-b border-dark-border flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Showing {mailingLists.length} of {totalMailingLists} lists. Use search to find others.
                </div>
              )}
              <Table
                data={listRows}
                columns={listColumns}
                searchable={false}
                onRowClick={(row) => setSelectedListId(row._id)}
                emptyMessage="No mailing lists"
                rowContextMenu={rowContextMenu}
              />
            </>
          )}
        </div>
      </div>

      {/* Create modal */}
      {isCreateModalOpen && (
        <MailingListFormModal
          bands={bands}
          onClose={() => setIsCreateModalOpen(false)}
          onSaved={() => {
            setIsCreateModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["mailing-lists"] });
          }}
        />
      )}

      {/* Edit modal */}
      {editingList && (
        <MailingListFormModal
          mailingList={editingList}
          bands={bands}
          onClose={() => setEditingList(null)}
          onSaved={() => {
            setEditingList(null);
            queryClient.invalidateQueries({ queryKey: ["mailing-lists"] });
          }}
        />
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Mailing List"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        danger
        isLoading={isDeleting}
      />
    </div>
  );
}

// --- Add Contact to List ---
function AddContactToList({
  mailingList,
  onAdded,
}: {
  mailingList: MailingList;
  onAdded: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const existingIds = useMemo(
    () => new Set(mailingList.contacts.map((c) => c.contact_id)),
    [mailingList.contacts]
  );

  // Server-side search for contacts
  const { data: searchResults = [] } = useQuery({
    queryKey: ["contacts-search-add", search],
    queryFn: async () => {
      if (!search.trim()) return [];
      const res = await contactsAPI.search(search, { page_size: 50 });
      return res.data.items;
    },
    enabled: isOpen && search.trim().length > 0,
  });

  const filteredContacts = useMemo(
    () => searchResults.filter((c) => !existingIds.has(c._id) && c.email),
    [searchResults, existingIds]
  );

  const handleAdd = async (contact: Contact) => {
    if (!contact.email) return;
    setIsAdding(true);
    try {
      const updatedContacts = [
        ...mailingList.contacts,
        { contact_id: contact._id, email: contact.email },
      ];
      await mailingListsAPI.update(mailingList._id, { contacts: updatedContacts });
      onAdded();
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setIsOpen(true)}>
        <Plus className="w-4 h-4" />
        Add Contact
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSearch("");
        }}
        title="Add Contact to List"
        size="md"
      >
        <div className="p-4 space-y-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search contacts by name or email..."
          />
          <div className="max-h-80 overflow-y-auto space-y-1">
            {!search.trim() ? (
              <p className="text-sm text-dark-text-secondary text-center py-4">
                Type to search for contacts to add.
              </p>
            ) : filteredContacts.length === 0 ? (
              <p className="text-sm text-dark-text-secondary text-center py-4">
                No matching contacts found.
              </p>
            ) : (
              filteredContacts.map((contact) => (
                <button
                  key={contact._id}
                  disabled={isAdding}
                  className="w-full text-left px-4 py-3 rounded-lg bg-dark-bg hover:bg-dark-border transition-colors flex items-center justify-between gap-3 disabled:opacity-50"
                  onClick={() => handleAdd(contact)}
                >
                  <div>
                    <div className="font-medium text-dark-text-primary text-sm">
                      {contact.name || "Unnamed"}
                    </div>
                    <div className="text-xs text-dark-text-muted">{contact.email}</div>
                  </div>
                  <Plus className="w-4 h-4 text-dark-text-muted shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
